import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  CustomQuestionSaveButton,
  ReportCustomQuestionTypePropsBase,
  StyledCustomQuestionType,
} from './utils';
import CustomQuestionTop from './CustomQuestionTop';
import { SelectorOptions } from 'shared/design-system/components/Selector';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { TextIntegerQuestionType } from 'admin/pages/ManageAccounts/CustomQuestions/utilts';
import { useSaveSurveyAnswer } from './hooks';
import CustomQuestionShow from './CustomQuestionShow';

const parseAnswer = (answer: ReportCustomQuestionTypePropsBase['answer']) => {
  const a = answer[0];
  if (!a)
    return {
      answerId: undefined,
      initialAnswerString: '',
      initialAnswers: [] as TextIntegerQuestionType[],
    };
  try {
    return {
      answerId: a.dbId,
      initialAnswerString: a.value,
      initialAnswers: JSON.parse(a.value) as TextIntegerQuestionType[],
    };
  } catch (e) {
    return {
      answerId: a.dbId,
      initialAnswerString: '',
      initialAnswers: [] as TextIntegerQuestionType[],
    };
  }
};

const StyledDiv = styled(StyledCustomQuestionType)``;

const CustomQuestionCheckbox: React.FC<ReportCustomQuestionTypePropsBase> = ({
  question,
  answer,
  disableEdit,
  initialEdit,
  saveButtonText,
  customQuestionIndex,
  customQuestion,
  questionIndex,
  reportId,
  onChange,
  handleAddDoneQuestionId,
  handleRemoveDoneQuestionId,
}) => {
  const { answerId, initialAnswers, initialAnswerString } = useMemo(
    () => parseAnswer(answer),
    [answer],
  );

  const {
    requestState,
    isEdit,
    handleShowEdit,
    handleCancelEdit,
    handleSaveSurveyAnswer,
  } = useSaveSurveyAnswer({
    onChange,
    initialAnswer: initialAnswerString,
    initialEdit,
    handleAddDoneQuestionId,
    handleRemoveDoneQuestionId,
    question,
    customQuestionIndex,
    customQuestion,
    questionIndex,
    reportId,
  });
  const options = question.questionStructure
    .structure as TextIntegerQuestionType[];
  const [selected, setSelected] = useState<TextIntegerQuestionType[]>(
    initialAnswers,
  );

  const handleClick = useCallback(
    (value: TextIntegerQuestionType, index: number) => {
      setSelected(s => {
        const newState = [...s];
        const i = newState.findIndex(x => x.label === value.label);
        if (i === -1) {
          newState.push(value);
        } else {
          newState.splice(i, 1);
        }
        return newState;
      });
    },
    [setSelected],
  );

  const handleSubmit = useCallback(() => {
    if (selected.length === 0) return;
    handleSaveSurveyAnswer({
      answerId,
      answer: {
        dbId: undefined,
        optionId: undefined,
        value: JSON.stringify(selected),
      },
    });
  }, [onChange, answerId, selected]);

  const handleCancel = useCallback(() => {
    setSelected(initialAnswers);
    handleCancelEdit();
  }, [initialAnswers, handleCancelEdit]);

  if (!isEdit || disableEdit) {
    return (
      <CustomQuestionShow
        answer={{
          questionType: 'checkbox',
          answer: selected,
        }}
        question={question}
        disableEdit={disableEdit}
        handleEdit={handleShowEdit}
      />
    );
  }

  return (
    <StyledDiv>
      <CustomQuestionTop
        title={question.questionText}
        helperText={question.helperText}
        questionType={question.questionType}
      />
      <SelectorOptions
        variant="checkbox"
        options={options}
        padding={SPACERS.tiny}
        getLabel={x => x.label}
        getSelected={val =>
          selected.findIndex(x => x.label === val.label) !== -1
        }
        onClick={handleClick}
      />
      <CustomQuestionSaveButton
        text={saveButtonText}
        disabled={selected.length === 0}
        requestState={requestState}
        handleSubmit={handleSubmit}
        handleCancel={initialAnswerString ? handleCancel : undefined}
      />
    </StyledDiv>
  );
};

export default CustomQuestionCheckbox;
